// Chapter 19 - Equivalence-based Instruction
// Source: Cooper, Heron, & Heward (3rd ed.), Chapter 19 (Pilgrim).
// All content traced to source-pdfs/Chapter-19-Equivalence-based-Instruction-8.pdf

import type { ChapterModule } from "./types";

const chapter19Module: ChapterModule = {
  meta: {
    id: "19",
    number: 19,
    title: "Equivalence-Based Instruction",
    subtitle: "Stimulus equivalence, reflexivity, symmetry, transitivity",
    part: "Part 8 - Developing New Behavior",
    estMinutes: 75,
    bacb: ["G-11"],
  },
  curated: true,
  overview: [
    "Chapter 19 (Pilgrim) covers the most generative discovery in modern behavior analysis: STIMULUS EQUIVALENCE. After teaching a small number of conditional discriminations between physically dissimilar stimuli (e.g., AB and BC), learners spontaneously demonstrate untrained RELATIONS that satisfy reflexivity (A=A), symmetry (if A=B then B=A), and transitivity (if A=B and B=C then A=C). When both symmetry and transitivity emerge, the stimuli form an EQUIVALENCE CLASS.",
    "You will learn the arbitrary matching-to-sample (MTS) procedure used to teach baselines and probe emergent relations, the four probe types (reflexivity, symmetry, transitivity, combined/equivalence), the procedures that build classes (class formation, class expansion, class merger, transfer of function, contextual control), key procedural variations (stimulus compounds, class-specific reinforcement, three-term contingency training), and how naming theory and relational frame theory (RFT) interpret these phenomena.",
  ],
  objectives: [
    { id: "G-11", text: "Apply equivalence-based instruction to teach functional skills and generative repertoires." },
  ],
  coreConcepts: [
    {
      title: "Stimulus equivalence and its origins",
      body: [
        "Stimulus equivalence is a behavior-analytic approach to symbolic function rooted in Sidman's (1971; Sidman & Cresson, 1973) reading-comprehension work. Equivalence makes possible a behavioral analysis of complex performances (categorization, semantic correspondence, creativity) without inventing mentalistic constructs.",
      ],
    },
    {
      title: "Arbitrary matching-to-sample (MTS)",
      body: [
        "MTS uses a discrete-trial four-term contingency with physically dissimilar stimuli. A trial begins with a sample (e.g., A1 or A2); an observing response brings up two or more comparisons (B1, B2); selecting the designated comparison earns a reinforcer. This teaches conditional discriminations: which comparison is correct depends on which sample is presented.",
      ],
    },
    {
      title: "Four probe types and the three mathematical relations",
      body: [
        "After teaching two interrelated conditional discriminations (e.g., AB and BC), probe trials test for the emergent relations that define equivalence. There are no consequences on probe trials; consistent patterns are described as EMERGENT or DERIVED.",
      ],
      bullets: [
        "Reflexivity tests: generalized identity matching with all training stimuli (AA, BB, CC). Mathematically: A = A.",
        "Symmetry tests: reversibility of sample and comparison (BA, CB). Mathematically: If A = B, then B = A.",
        "Transitivity tests: relations through a nodal stimulus (AC). Mathematically: If A = B and B = C, then A = C.",
        "Combined / equivalence tests: symmetry plus transitivity (CA).",
      ],
    },
    {
      title: "Equivalence class and significance",
      body: [
        "Positive outcomes on all probe types document INTERCHANGEABILITY among a group of stimuli; the stimuli form an equivalence class. Equivalence is significant because (a) many stimulus relations emerge from a few taught relations (the 'bang for your buck' effect), and (b) the defining criteria provide an operational definition of symbolic function.",
      ],
    },
    {
      title: "Key EBI outcomes",
      body: [
        "CLASS FORMATION: percentage of probe trials with class-consistent selection. DELAYED EMERGENCE: equivalence performances often grow stronger with repeated testing. CLASS EXPANSION: increasing class size by teaching a relation between a novel stimulus and a class member. CLASS MERGER: combining classes by teaching a relation between members of separate classes. TRANSFER OF FUNCTION: a new function for one member transfers to all members.",
      ],
    },
    {
      title: "Contextual control and the five-term contingency",
      body: [
        "Contextual control requires three levels of antecedent stimuli, allowing the same stimuli to belong to MORE THAN ONE equivalence class depending on context. Contextual control training uses a five-term contingency.",
      ],
    },
    {
      title: "Designing EBI: training decisions",
      body: [
        "Number/type of stimuli, mode of presentation, observing response, content of instructions, approach to balanced trial presentations, special training steps, training STRUCTURE (linear, one-to-many, many-to-one), mastery criteria, and consequence arrangement.",
      ],
    },
    {
      title: "Designing EBI: testing decisions",
      body: [
        "Composition of probe-trial blocks, arrangement of consequences in probe blocks, order of probe-type presentation, and criteria for judging class formation.",
      ],
    },
    {
      title: "Procedural variations",
      body: [
        "STIMULUS COMPOUNDS: two-part samples produce class membership for each element. CLASS-SPECIFIC REINFORCEMENT: unique reinforcers per class can themselves become class members, expanding the class. THREE-TERM CONTINGENCY TRAINING: simple discriminations may be easier than conditional discriminations for some learners and can still produce class formation.",
      ],
    },
    {
      title: "Applications across populations",
      body: [
        "EBI has shown generality across children and adults with intellectual disabilities, typically developing children, adult clinical populations (health behaviors, brain injury), gerontology, and university course content (math, music, Braille reading, academic content).",
      ],
    },
    {
      title: "Alternative theoretical accounts",
      body: [
        "NAMING THEORY views naming as a higher-order bidirectional unit of speaker/listener behavior; it explains equivalence as a byproduct of basic verbal repertoires (intervention targets listener behavior and tacting). RELATIONAL FRAME THEORY (RFT) views relational framing as the higher-order operant established by MULTIPLE-EXEMPLAR TRAINING; equivalence is one frame among many, and applications use multiple-exemplar training to build generalized relational repertoires.",
      ],
    },
  ],
  glossary: [
    { term: "Stimulus equivalence", definition: "An emergent set of stimulus relations - reflexivity, symmetry, and transitivity - that establishes interchangeability among stimuli in a class.", sourceHint: "Stimulus equivalence describes a behavior analytic approach to understanding and establishing symbolic function." },
    { term: "Matching-to-sample (MTS) procedure", definition: "Discrete-trial four-term contingency: sample, observing response, comparison array, and selection of the designated comparison.", sourceHint: "Arbitrary match-to-sample training involves a discrete-trial four-term contingency arrangement with physically dissimilar stimuli." },
    { term: "Conditional discrimination", definition: "A discrimination in which the correct comparison depends on the sample presented.", sourceHint: "Arbitrary match-to-sample training produces conditional discriminations, in which discrimination between the comparison stimuli is conditional on the sample presented." },
    { term: "Reflexivity", definition: "Generalized identity matching with all training stimuli (AA, BB, CC); mathematically A = A.", sourceHint: "reflexivity tests, that is, tests for generalized identity matching with all training stimuli, or AA, BB, and CC; in mathematical set theory, reflexivity is defined as A = A." },
    { term: "Symmetry", definition: "Emergent reversibility of sample and comparison functions (BA, CB); If A = B then B = A.", sourceHint: "symmetry tests for the reversibility of sample and comparison functions, or BA and CB." },
    { term: "Transitivity", definition: "Emergent relation between stimuli never paired during training, through a nodal stimulus (AC); If A = B and B = C then A = C.", sourceHint: "transitivity tests for relations between stimuli that were never presented together during training but that share a trained relation with a nodal stimulus, or AC." },
    { term: "Equivalence test", definition: "Combined test of symmetry plus transitivity (CA) that confirms equivalence-class formation.", sourceHint: "combined or equivalence tests in which both symmetry and transitivity are evaluated simultaneously, or CA." },
    { term: "Nodal stimulus (node)", definition: "A stimulus that links two trained relations (e.g., B is the node when AB and BC are trained).", sourceHint: "stimuli that... share a trained relation with a nodal stimulus." },
    { term: "Derived stimulus relations", definition: "Untrained stimulus relations that emerge from a small number of taught relations.", sourceHint: "derived stimulus relations (Key Terms list)." },
    { term: "Emergent stimulus relations", definition: "Untrained relations observed during probe trials without programmed consequences.", sourceHint: "consistent patterns of responding on these trial types are described as emergent or derived." },
    { term: "Class formation", definition: "Confirmation that a set of stimuli function as an equivalence class via reflexivity, symmetry, and transitivity probes.", sourceHint: "class formation - typically measured as the percentage of probe trials on which a comparison selection is consistent with equivalence." },
    { term: "Class expansion", definition: "Increasing class size by teaching a new relation between a novel stimulus and an established class member.", sourceHint: "class expansion - increasing the size of an established equivalence class to include an additional member or members." },
    { term: "Class merger", definition: "Combining independent equivalence classes by teaching a relation between members of each.", sourceHint: "class merger - a combining of independent equivalence classes such that a larger class is established." },
    { term: "Transfer of function", definition: "Teaching a new function for one member of an established class results in the same function for all members.", sourceHint: "transfer of function - the finding that teaching a new function for one member of an established equivalence class results in the same function holding for all members of the class." },
    { term: "Contextual control", definition: "Five-term contingency in which the functions of stimuli in a conditional discrimination vary depending on context.", sourceHint: "contextual control - stimulus control requiring three levels of antecedent stimuli, such that the functions of the stimuli in a conditional discrimination vary depending on the context." },
    { term: "Higher-order operant class", definition: "An operant class whose members are themselves operants (e.g., a class of relational responses).", sourceHint: "higher-order operant class (Key Terms list)." },
    { term: "Training structure", definition: "The pattern of relations taught during baseline (linear, one-to-many, many-to-one); influences emergence.", sourceHint: "the type of training structure to be used." },
    { term: "Class-specific reinforcement", definition: "Reinforcer paired uniquely with a particular class; can itself become a class member.", sourceHint: "class-specific reinforcement - providing a unique reinforcer for discriminated responses as a function of the antecedent's intended class." },
    { term: "Delayed emergence", definition: "Equivalence performance often strengthens with repeated testing.", sourceHint: "delayed emergence - a relatively common observation in which equivalence performances grow stronger with repeated testing." },
  ],
  procedures: [
    {
      title: "Establish an equivalence class with AB-BC training",
      intro: "Two interrelated conditional discriminations and four probe types.",
      steps: [
        { title: "Teach AB", detail: "Train: A1 sample -> select B1; A2 sample -> select B2. Reinforce correct comparisons." },
        { title: "Teach BC", detail: "Train: B1 sample -> select C1; B2 sample -> select C2." },
        { title: "Probe reflexivity (AA, BB, CC)", detail: "Test generalized identity matching with no consequences." },
        { title: "Probe symmetry (BA, CB)", detail: "Test reversal: B1 sample -> select A1; C1 sample -> select B1." },
        { title: "Probe transitivity (AC)", detail: "Test through node B: A1 sample -> select C1." },
        { title: "Probe equivalence (CA)", detail: "Test combined symmetry + transitivity: C1 sample -> select A1." },
        { title: "Class confirmed", detail: "If all probes pass, the stimuli A, B, C form an equivalence class." },
      ],
    },
    {
      title: "Expand or merge equivalence classes",
      intro: "Use class expansion or class merger to grow generativity.",
      steps: [
        { title: "Class expansion", detail: "Teach a new relation (e.g., AD); probe emergent BD, DA, DB, CD, DC to confirm D is in the class." },
        { title: "Class merger", detail: "Teach one relation between members of two existing classes (e.g., A1-X1); probe emergent relations between formerly independent stimuli." },
      ],
    },
    {
      title: "Build contextual control",
      intro: "Use a five-term contingency to allow context-dependent class membership.",
      steps: [
        { title: "Choose context cues", detail: "Pick two context stimuli (X1, X2) that distinguish two possible class assignments for the same stimuli." },
        { title: "Train each context", detail: "Under X1, train AB; under X2, train AC. Reinforce context-appropriate selections." },
        { title: "Probe context-dependent emergent relations", detail: "Verify that the same stimuli function differently across contexts." },
      ],
    },
  ],
  examples: [
    { scenario: "A learner is taught the spoken word 'cat' (A1) matches the picture (B1) and the picture matches the written word 'CAT' (C1).",
      application: "AB and BC training. After mastery, probe shows the learner can also match picture to spoken word (BA), written word to picture (CB), spoken to written (AC), and written to spoken (CA) - an equivalence class.",
      sourceHint: "After teaching two interrelated conditional discriminations (e.g., AB and BC), probe trials are presented to test for emergence of the untrained relations that define equivalence." },
    { scenario: "After equivalence is established, the teacher reinforces excited behavior when the spoken word 'cat' is heard; the same excited behavior emerges to the picture and written word without further training.",
      application: "Transfer of function - new function for one class member spreads to all members.",
      sourceHint: "transfer of function - the finding that teaching a new function for one member of an established equivalence class results in the same function holding for all members of the class." },
    { scenario: "A learner shows weak equivalence probes on day 1; testing is repeated daily; by day 4 all probes are at ceiling.",
      application: "Delayed emergence - equivalence often strengthens with repeated probes.",
      sourceHint: "delayed emergence - a relatively common observation in which equivalence performances grow stronger with repeated testing." },
    { scenario: "Same stimulus 'red' is in an 'emotion' equivalence class under one context (anger) but in a 'color' equivalence class under another.",
      application: "Contextual control with a five-term contingency.",
      sourceHint: "contextual control - stimulus control requiring three levels of antecedent stimuli, such that the functions of the stimuli in a conditional discrimination vary depending on the context." },
  ],
  comparisons: [
    {
      title: "Reflexivity vs Symmetry vs Transitivity",
      left:  { label: "Reflexivity", points: [
        "Identity matching (A = A).",
        "Each training stimulus matches itself.",
      ]},
      right: { label: "Symmetry / Transitivity", points: [
        "Symmetry: bi-directional (BA, CB).",
        "Transitivity: indirect through node (AC).",
        "Combined equivalence: CA confirms class.",
      ]},
    },
    {
      title: "Class expansion vs Class merger",
      left:  { label: "Class expansion", points: [
        "Add a new stimulus to an existing class.",
        "Teach one new relation, verify emergence of the rest.",
      ]},
      right: { label: "Class merger", points: [
        "Combine two existing classes into one.",
        "Teach one relation between members of each class.",
      ]},
    },
    {
      title: "Sidman vs Naming theory vs RFT",
      left:  { label: "Sidman / classical equivalence", points: [
        "Equivalence is a direct outcome of reinforcement contingencies.",
        "Operational test: reflexivity + symmetry + transitivity.",
      ]},
      right: { label: "Naming theory / RFT", points: [
        "Naming: bidirectional speaker/listener operant produces equivalence.",
        "RFT: relational framing is the higher-order operant; multiple-exemplar training builds it.",
      ]},
    },
  ],
  misconceptions: [
    {
      myth: "Reflexivity is just trivial identity matching.",
      reality: "Reflexivity probes test GENERALIZED identity matching across all training stimuli; failure to show reflexivity prevents conclusion of equivalence.",
      sourceHint: "reflexivity tests, that is, tests for generalized identity matching with all training stimuli, or AA, BB, and CC.",
    },
    {
      myth: "If a learner shows symmetry, equivalence is established.",
      reality: "Equivalence requires reflexivity AND symmetry AND transitivity (or the combined CA test). Symmetry alone does not establish the class.",
      sourceHint: "Positive outcomes on all probe types document interchangeability among a given group of stimuli and allow the conclusion that an equivalence class has formed.",
    },
    {
      myth: "Equivalence classes are static once formed.",
      reality: "Classes can expand (class expansion), merge (class merger), or split via contextual control. Functions can also transfer across members.",
      sourceHint: "class expansion... class merger... transfer of function... contextual control.",
    },
    {
      myth: "Equivalence requires extensive training of every relation.",
      reality: "The hallmark is GENERATIVITY: a few trained relations produce many emergent ones. Training all relations defeats the point.",
      sourceHint: "Many stimulus relations are generated after teaching only a small number (i.e., there is an impressive 'bang for your buck' effect).",
    },
    {
      myth: "Equivalence-based instruction only works with cognitively typical learners.",
      reality: "EBI has been demonstrated across populations including children and adults with intellectual disabilities, brain-injured adults, gerontology populations, and typically developing learners.",
      sourceHint: "Work with children and adults with intellectual disabilities, as well as young typically developing children, represents the largest category of EBI applications.",
    },
  ],
  examChecklist: [
    "Define stimulus equivalence and state the three mathematical relations it requires.",
    "Describe the arbitrary MTS procedure.",
    "Describe reflexivity, symmetry, transitivity, and the combined equivalence probe with stimulus letter labels.",
    "Define class formation, class expansion, class merger, and transfer of function.",
    "Define contextual control and its five-term contingency.",
    "List training decisions for EBI procedure design.",
    "List testing decisions for EBI procedure design.",
    "Describe stimulus compounds, class-specific reinforcement, and three-term contingency variations.",
    "Contrast Sidman's account with naming theory and RFT.",
    "Give one EBI application across two different populations or content domains.",
  ],
  flashcardTerms: [
    "Stimulus equivalence", "Matching-to-sample (MTS) procedure", "Conditional discrimination",
    "Reflexivity", "Symmetry", "Transitivity", "Equivalence test",
    "Nodal stimulus (node)", "Derived stimulus relations", "Emergent stimulus relations",
    "Class formation", "Class expansion", "Class merger", "Transfer of function",
    "Contextual control", "Higher-order operant class", "Training structure",
    "Class-specific reinforcement", "Delayed emergence",
  ],
  quiz: [
    {
      id: "Q1",
      question: "If a learner is taught AB and BC and then accurately matches A1 to C1 without further training, this is a:",
      options: [
        { label: "A", text: "Reflexivity test" },
        { label: "B", text: "Symmetry test" },
        { label: "C", text: "Transitivity test" },
        { label: "D", text: "Conditional discrimination training trial" },
      ],
      answer: "C",
      explanation: "AC matching with B as the node tests transitivity: If A=B and B=C, then A=C.",
      topic: "Probe types",
      sourceHint: "transitivity tests for relations between stimuli that were never presented together during training but that share a trained relation with a nodal stimulus, or AC.",
    },
    {
      id: "Q2",
      question: "Stimulus equivalence requires which combination of probe outcomes?",
      options: [
        { label: "A", text: "Reflexivity only" },
        { label: "B", text: "Reflexivity AND symmetry" },
        { label: "C", text: "Reflexivity, symmetry, AND transitivity" },
        { label: "D", text: "Only the trained AB and BC relations" },
      ],
      answer: "C",
      explanation: "Equivalence is documented by positive outcomes on reflexivity, symmetry, AND transitivity (or the combined CA test).",
      topic: "Equivalence definition",
      sourceHint: "Positive outcomes on all probe types document interchangeability among a given group of stimuli and allow the conclusion that an equivalence class has formed.",
    },
    {
      id: "Q3",
      question: "A child learns that 'cat' refers to a cat picture; later, hearing the spoken word evokes the same excited behavior the cat picture does. This is best classified as:",
      options: [
        { label: "A", text: "Class merger" },
        { label: "B", text: "Class expansion" },
        { label: "C", text: "Transfer of function" },
        { label: "D", text: "Contextual control" },
      ],
      answer: "C",
      explanation: "When a function (excited behavior) taught to one class member appears for other members without direct teaching, that is transfer of function.",
      topic: "Transfer of function",
      sourceHint: "transfer of function - the finding that teaching a new function for one member of an established equivalence class results in the same function holding for all members of the class.",
    },
    {
      id: "Q4",
      question: "A new stimulus D is added to an existing class containing A, B, and C by teaching one new relation. This is:",
      options: [
        { label: "A", text: "Class merger" },
        { label: "B", text: "Class expansion" },
        { label: "C", text: "Class transformation" },
        { label: "D", text: "Contextual control" },
      ],
      answer: "B",
      explanation: "Class expansion adds new members to an existing class via a single new trained relation, with emergent relations to the rest of the class.",
      topic: "Class expansion",
      sourceHint: "class expansion - increasing the size of an established equivalence class to include an additional member or members.",
    },
    {
      id: "Q5",
      question: "Contextual control over equivalence requires:",
      options: [
        { label: "A", text: "Two stimuli per class" },
        { label: "B", text: "A four-term contingency" },
        { label: "C", text: "A five-term contingency" },
        { label: "D", text: "Unlimited classes per stimulus" },
      ],
      answer: "C",
      explanation: "Contextual control adds a third level of antecedent stimulus, producing a five-term contingency. Same stimuli can belong to different classes under different contexts.",
      topic: "Contextual control",
      sourceHint: "contextual control - stimulus control requiring three levels of antecedent stimuli, such that the functions of the stimuli in a conditional discrimination vary depending on the context. Contextual control training requires a five-term contingency.",
    },
    {
      id: "Q6",
      question: "Naming theory and RFT differ from Sidman's account by emphasizing:",
      options: [
        { label: "A", text: "That equivalence is a direct outcome of basic reinforcement contingencies" },
        { label: "B", text: "That equivalence requires a special learning history (naming or multiple-exemplar relational training)" },
        { label: "C", text: "That equivalence cannot be produced in humans" },
        { label: "D", text: "That equivalence is purely mentalistic" },
      ],
      answer: "B",
      explanation: "Both naming theory and RFT argue that equivalence emerges from a special learning history rather than from direct contingencies alone.",
      topic: "Theoretical accounts",
      sourceHint: "Both emphasize that novel responding is due to a special learning history, rather than a direct outcome of a reinforcement contingency (as in Sidman's view).",
    },
    {
      id: "Q7",
      question: "Delayed emergence describes:",
      options: [
        { label: "A", text: "Symmetry tests failing first time but improving with repeated testing" },
        { label: "B", text: "An equivalence class collapsing" },
        { label: "C", text: "A new class being formed late in training" },
        { label: "D", text: "Loss of transfer of function" },
      ],
      answer: "A",
      explanation: "Delayed emergence is the observation that equivalence performances often grow stronger with repeated testing, even without programmed consequences.",
      topic: "Delayed emergence",
      sourceHint: "delayed emergence - a relatively common observation in which equivalence performances grow stronger with repeated testing.",
    },
  ],
  reviewPrompts: [
    "Diagram the AB-BC training plan and the four probe types with arrows showing each relation.",
    "Explain how class expansion and class merger differ procedurally.",
    "Apply transfer of function to an equivalence class in a real teaching task.",
    "Design contextual control for a stimulus that should belong to two different classes.",
    "Compare Sidman's, naming theory's, and RFT's explanations of derived relational responding.",
    "Identify a learner population where EBI might offer faster acquisition than direct teaching.",
  ],
  recap: [
    "Stimulus equivalence emerges when reflexivity (A=A), symmetry (A=B implies B=A), and transitivity (A=B and B=C imply A=C) are all demonstrated after training a small number of conditional discriminations. The result is an equivalence class whose members are functionally interchangeable.",
    "Equivalence-based instruction is highly generative. Key outcomes include class formation, delayed emergence, class expansion, class merger, transfer of function, and contextual control. Procedural variations (stimulus compounds, class-specific reinforcement, three-term contingency training) broaden applicability.",
    "Naming theory and Relational Frame Theory offer alternative accounts that emphasize a special learning history (bidirectional naming or multiple-exemplar relational training). Applications span intellectual disability, brain injury, gerontology, and university course content.",
  ],
  sourceExcerpts: [
    {
      heading: "Equivalence definition",
      text: "Stimulus equivalence describes a behavior analytic approach to understanding and establishing symbolic function. Positive outcomes on all probe types document interchangeability among a given group of stimuli and allow the conclusion that an equivalence class has formed. The equivalence class consists of all such interchangeable stimuli.",
    },
    {
      heading: "Three mathematical relations",
      text: "Reflexivity tests, that is, tests for generalized identity matching with all training stimuli, or AA, BB, and CC; in mathematical set theory, reflexivity is defined as A = A. Symmetry tests for the reversibility of sample and comparison functions, or BA and CB. Transitivity tests for relations between stimuli that were never presented together during training but that share a trained relation with a nodal stimulus, or AC.",
    },
    {
      heading: "EBI key outcomes",
      text: "class formation... delayed emergence - a relatively common observation in which equivalence performances grow stronger with repeated testing. class expansion - increasing the size of an established equivalence class to include an additional member. class merger - a combining of independent equivalence classes such that a larger class is established. transfer of function - the finding that teaching a new function for one member of an established equivalence class results in the same function holding for all members.",
    },
    {
      heading: "Naming theory and RFT",
      text: "Naming theory and relational frame theory (RFT) provide differing accounts of the basis for novel relational responding. Both emphasize that novel responding is due to a special learning history, rather than a direct outcome of a reinforcement contingency (as in Sidman's view).",
    },
  ],
};

export default chapter19Module;
